import React, { useState, useEffect, useRef } from "react";
import { Button, Table, Modal, Form, Pagination } from "react-bootstrap";
import Swal from "sweetalert2";
import { MdFilterAlt, MdFilterAltOff } from "react-icons/md";
import callingleads from "../../assets/images/Lead Formate.csv";
import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { BsThreeDots, BsFiletypeXlsx } from "react-icons/bs";
import { MdSecurityUpdateGood } from "react-icons/md";

const API_URL = process.env.REACT_APP_API_URL;

function UploadPropertyLeadCSV() {
  const location = useLocation();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [filters, setFilters] = useState({
    name: "",
    mobile: "",
    requirement: "",
    status: "",
    from_date: "",
    to_date: "",
  });

  const [isFilterActive, setIsFilterActive] = useState(false);
  const handleToggle = () => setIsFilterActive(!isFilterActive);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const inputRef = useRef(null);

  const token = localStorage.getItem("token");
  const [editData, setEditData] = useState({
    id: "",
    status: "",
    remark: "",
    next_follow_up_date: "",
    followup_time: "",
  });

  const isNewStatusPage = () => {
    const params = new URLSearchParams(location.search);
    return params.get("status") === "New";
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get("status");
    if (statusParam) {
      setIsFilterActive(true);
      setFilters((prev) => ({ ...prev, status: statusParam }));
      fetchLeads({ ...filters, status: statusParam }, page);
    } else {
      fetchLeads(filters, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const toSentenceCase = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handleCheckboxChange = (leadId) => {
    setSelectedLeads((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedLeads([]);
    } else {
      const allLeadIds = leads.map((lead) => lead.id);
      setSelectedLeads(allLeadIds);
    }
    setSelectAll(!selectAll);
  };

  const handleDeleteSelected = async () => {
    if (selectedLeads.length === 0) {
      Swal.fire(
        "Warning",
        "Please select at least one lead to delete",
        "warning"
      );
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `If you delete this lead, you can not revert it back , please verify and then delete ${selectedLeads.length} lead(s)`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Delete",
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_URL}/lead-delete-list-only-new`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ids: selectedLeads }),
        });

        const data = await res.json();

        if (res.ok && data?.status == "1") {
          Swal.fire(
            "Success",
            data?.message || "Leads deleted successfully!",
            "success"
          );
          setSelectedLeads([]);
          setSelectAll(false);
          navigate("/calling-lead-report");
          fetchLeads(filters, page);
        } else {
          Swal.fire(
            "Error",
            data?.message || "Failed to delete leads",
            "error"
          );
        }
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Network error while deleting leads", "error");
      }
    }
  };

  const fetchLeads = async (searchParams = {}, currentPage = 1) => {
    setListLoading(true);
    try {
      const params = new URLSearchParams({
        ...searchParams,
        page: currentPage,
      }).toString();

      const res = await fetch(`${API_URL}/lead-csv-list?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (res.ok && data?.status === "1") {
        setLeads(data.data || []);
        setPage(currentPage);
        setTotalPages(data.totalPages || 1);
        setSelectedLeads([]);
        setSelectAll(false);
      } else {
        Swal.fire("Error", data?.message || "Failed to fetch leads", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Network error while fetching leads", "error");
    } finally {
      setListLoading(false);
    }
  };

  const handleSearch = () => {
    fetchLeads(filters, 1);
  };

  const handlePageChange = (newPage) => {
    fetchLeads(filters, newPage);
  };

  const handleUploadSubmit = async () => {
    if (!file) {
      Swal.fire("Warning", "Please select a CSV file!", "warning");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("leadCsv", file);

      const res = await fetch(`${API_URL}/lead-create-csv`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();

      if (res.ok && data?.success === "1") {
        Swal.fire(
          "Success",
          data?.message || "CSV uploaded successfully!",
          "success"
        );
        setShowModal(false);
        setFile(null);
        fetchLeads(filters, page);
      } else {
        Swal.fire("Error", data?.message || "Failed to upload CSV", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err.message || "Network error while uploading CSV",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditLead = async (leadId) => {
    try {
      const res = await fetch(`${API_URL}/lead-csv-edit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: leadId }),
      });

      const data = await res.json();

      if (res.ok && data?.status === "1" && data?.data?.length > 0) {
        const lead = data.data[0];

        setEditData({
          id: lead.id,
          name: lead.name || "",
          mobile: lead.mobile || "",
          alt_mobile: lead.alt_mobile || "",
          city: lead.city || "",
          job_title: lead.job_title || "",
          budget: lead.budget || "",
          requirement: lead.requirement || "",
          status: lead.status || "",
          remark: lead.remark || "",
          followup_time: lead.followup_time || "",
          next_follow_up_date: lead.next_follow_up_date || "",
          assign_by_subadmin: lead.assign_by_subadmin || "",
          assign_by_subadmin_email: lead.assign_by_subadmin_email || "",
        });

        setShowEditModal(true);
      } else {
        Swal.fire(
          "Error",
          data?.message || "Failed to fetch lead details",
          "error"
        );
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Network error while fetching lead details", "error");
    }
  };

  const handleUpdateLead = async () => {
    try {
      const res = await fetch(`${API_URL}/lead-csv-update`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editData),
      });

      const data = await res.json();

      if (res.ok && data?.success === "1") {
        Swal.fire(
          "Success",
          data?.message || "Lead updated successfully!",
          "success"
        );
        setShowEditModal(false);
        fetchLeads(filters, page);
      } else {
        Swal.fire("Error", data?.message || "Failed to update lead", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Network error while updating lead", "error");
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/lead-csv-list-export`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch data");
      const json = await res.json();
      const ws = XLSX.utils.json_to_sheet(json.data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Leads");
      XLSX.writeFile(wb, "SocialMediaCallingLeads.xlsx");
    } catch (err) {
      console.error(err);
      alert("Excel download failed");
    }
  };

const EmptyState = () => (
  <div
    style={{
      padding: "60px 20px",
      textAlign: "center",
      color: "#6c757d",
      fontSize: "15px",
      fontWeight: 500,
      background: "#f8f9fa",
      borderRadius: "10px",
      border: "1px dashed #dee2e6",
    }}
  >
    No leads found
  </div>
);

  // ═══════════ INLINE STYLES ═══════════
  const gradientBg =
    "linear-gradient(135deg, #1e1b4b 0%, #3b0f6b 50%, #4c1d95 100%)";

  const styles = {
    filterToggle: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "42px",
      height: "42px",
      padding: 0,
      border: isFilterActive
        ? "1px solid #ffffff"
        : "1px solid rgba(255, 255, 255, 0.3)",
      background: isFilterActive ? "#ffffff" : "rgba(255, 255, 255, 0.15)",
      color: isFilterActive ? "#1e1b4b" : "#ffffff",
      borderRadius: "10px",
      fontSize: "18px",
      cursor: "pointer",
      transition: "all 0.25s ease",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      backdropFilter: "blur(6px)",
    },
    downloadBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      height: "42px",
      padding: "0 20px",
      fontSize: "13.5px",
      fontWeight: 700,
      color: "#fff",
      background: gradientBg,
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 14px rgba(76, 29, 149, 0.35)",
      transition: "all 0.22s ease",
      letterSpacing: "0.02em",
    },
    filterInput: {
      flex: "1 1 180px",
      minWidth: 0,
      height: "44px",
      padding: "0 14px",
      fontSize: "13.5px",
      border: "1.5px solid #e2e8f0",
      borderRadius: "10px",
      background: "#fff",
      color: "#0f172a",
      outline: "none",
      boxSizing: "border-box",
      margin: 0,
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    },
    filterSelect: {
      flex: "1 1 180px",
      minWidth: 0,
      height: "44px",
      padding: "0 40px 0 14px",
      fontSize: "13.5px",
      border: "1.5px solid #e2e8f0",
      borderRadius: "10px",
      background: "#fff",
      color: "#0f172a",
      outline: "none",
      boxSizing: "border-box",
      margin: 0,
      cursor: "pointer",
      appearance: "none",
      backgroundImage:
        "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E\")",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 14px center",
      backgroundSize: "14px",
    },
    searchBtn: {
      flexShrink: 0,
      height: "44px",
      padding: "0 24px",
      fontSize: "13.5px",
      fontWeight: 700,
      color: "#fff",
      background: gradientBg,
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 14px rgba(76, 29, 149, 0.35)",
      transition: "all 0.22s ease",
      margin: 0,
    },
  };

  const applyFocus = (e) => {
    e.currentTarget.style.borderColor = "#6366f1";
    e.currentTarget.style.boxShadow = "0 0 0 4px rgba(99, 102, 241, 0.12)";
  };

  const removeFocus = (e) => {
    e.currentTarget.style.borderColor = "#e2e8f0";
    e.currentTarget.style.boxShadow = "none";
  };

  return (
    <div className="card mt-2">
      <div className="card-header flex-wrap-mobile d-flex justify-content-between align-items-md-center gap-2">
        <div className="titlepage">
          <h3 className="m-0">Social Media Leads</h3>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {/* ═══════════ DOWNLOAD BUTTON ═══════════ */}
          <button
            onClick={handleDownloadExcel}
            style={styles.downloadBtn}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 8px 20px rgba(76, 29, 149, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 14px rgba(76, 29, 149, 0.35)";
            }}
          >
            <BsFiletypeXlsx style={{ fontSize: "16px" }} />
            Download Excel
          </button>

          {isNewStatusPage() && selectedLeads.length > 0 && (
            <Button variant="danger" onClick={handleDeleteSelected}>
              Delete Selected ({selectedLeads.length})
            </Button>
          )}

          {/* ═══════════ FILTER TOGGLE ═══════════ */}
          <button
            onClick={handleToggle}
            title={isFilterActive ? "Hide Filters" : "Show Filters"}
            style={styles.filterToggle}
            onMouseEnter={(e) => {
              if (!isFilterActive) {
                e.currentTarget.style.background =
                  "rgba(255, 255, 255, 0.25)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isFilterActive) {
                e.currentTarget.style.background =
                  "rgba(255, 255, 255, 0.15)";
                e.currentTarget.style.transform = "translateY(0)";
              }
            }}
          >
            {isFilterActive ? <MdFilterAltOff /> : <MdFilterAlt />}
          </button>
        </div>
      </div>

      <div className="card-body">
        {/* ═══════════ FILTER SECTION ═══════════ */}
        {isFilterActive && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              alignItems: "center",
              marginBottom: "20px",
              width: "100%",
            }}
          >
            <input
              type="text"
              placeholder="Name"
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              style={styles.filterInput}
              onFocus={applyFocus}
              onBlur={removeFocus}
            />
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) =>
                setFilters({ ...filters, from_date: e.target.value })
              }
              style={styles.filterInput}
              onFocus={applyFocus}
              onBlur={removeFocus}
            />
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) =>
                setFilters({ ...filters, to_date: e.target.value })
              }
              style={styles.filterInput}
              onFocus={applyFocus}
              onBlur={removeFocus}
            />
            <input
              type="text"
              placeholder="Mobile"
              value={filters.mobile}
              onChange={(e) =>
                setFilters({ ...filters, mobile: e.target.value })
              }
              style={styles.filterInput}
              onFocus={applyFocus}
              onBlur={removeFocus}
            />
            <input
              type="text"
              placeholder="Requirement"
              value={filters.requirement}
              onChange={(e) =>
                setFilters({ ...filters, requirement: e.target.value })
              }
              style={styles.filterInput}
              onFocus={applyFocus}
              onBlur={removeFocus}
            />
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              style={styles.filterSelect}
              onFocus={applyFocus}
              onBlur={removeFocus}
            >
              <option value="">All Status</option>
              <option value="Already Purchased">Already Purchased</option>
              <option value="Call not Pickup">Call not Pickup</option>
              <option value="Customer Self Call Back When He Will Come">
                Customer Self Call Back When He Will Come
              </option>
              <option value="In Follow Up">In Follow Up</option>
              <option value="Details sent visit not done">
                Details sent visit not done
              </option>
              <option value="In Follow Up Booking Done">
                In Follow Up Booking Done
              </option>
              <option value="In Follow Up Booking Done Payment Pending">
                In Follow Up Booking Done Payment Pending
              </option>
              <option value="In Follow Up Hot">In Follow Up Hot</option>
              <option value="In Follow Up Site Visit Done Booking Pending">
                In Follow Up Site Visit Done Booking Pending
              </option>
              <option value="Invalid Mobile Number">
                Invalid Mobile Number
              </option>
              <option value="Looking in Commercial Use">
                Looking in Commercial Use
              </option>
              <option value="Looking in Different Location">
                Looking in Different Location
              </option>
              <option value="Looking in Low Budget">
                Looking in Low Budget
              </option>
              <option value="M Profile">M Profile</option>
              <option value="Not Interested">Not Interested</option>
              <option value="Not Responding">Not Responding</option>
              <option value="New">New</option>
            </select>
            <button
              onClick={handleSearch}
              style={styles.searchBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 20px rgba(76, 29, 149, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 14px rgba(76, 29, 149, 0.35)";
              }}
            >
              Search
            </button>
          </div>
        )}

        {listLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading leads...</p>
          </div>
        ) : leads.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  {isNewStatusPage() && (
                    <th>
                      Check All
                      <Form.Check
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                  )}
                  <th>#</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Alt Mobile</th>
                  <th>City</th>
                  <th>Lead Date</th>
                  <th>Budget</th>
                  <th>Assign By</th>
                  <th>Assign To</th>
                  <th>Job Title</th>
                  <th>PlatForm</th>
                  <th>Requirement</th>
                  <th>Remark</th>
                  <th>Status</th>
                  <th>Next Follow Up Date</th>
                  <th>Next Follow Up Timing</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, idx) => (
                  <tr key={lead.id || idx}>
                    {isNewStatusPage() && (
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleCheckboxChange(lead.id)}
                        />
                      </td>
                    )}
                    <td>{(page - 1) * 100 + idx + 1}</td>
                    <td>{toSentenceCase(lead.name)}</td>
                    <td>{lead.mobile}</td>
                    <td>{lead.alt_mobile || "-"}</td>
                    <td>{toSentenceCase(lead.city)}</td>
                    <td>
                      {(() => {
                        if (!lead.lead_date) return "—";
                        const date = new Date(lead.lead_date);
                        if (isNaN(date)) return "Invalid Date";
                        const day = String(date.getDate()).padStart(2, "0");
                        const month = String(date.getMonth() + 1).padStart(
                          2,
                          "0"
                        );
                        const year = date.getFullYear();
                        return `${day}-${month}-${year}`;
                      })()}
                    </td>
                    <td>{toSentenceCase(lead.budget) || "-"}</td>
                    <td>
                      <div className="table-cell-remark">
                        {lead.assign_by_subadmin_email
                          ? lead.assign_by_subadmin_email
                          : "-"}
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-remark">
                        {lead.assign_to_subadmin_email
                          ? lead.assign_to_subadmin_email
                          : "-"}
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-remark">
                        {toSentenceCase(lead.job_title)}
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-remark">
                        {toSentenceCase(lead.plat_form)}
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-remark">
                        {toSentenceCase(lead.requirement)}
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-remark">
                        {toSentenceCase(lead.remark) || "-"}
                      </div>
                    </td>
                    <td>
                      <strong
                        style={{
                          color: lead.status === "New" ? "green" : "red",
                          fontWeight: "bold",
                        }}
                      >
                        {toSentenceCase(lead.status)}
                      </strong>
                    </td>
                    <td>
                      {(() => {
                        if (!lead.next_follow_up_date) return "—";
                        let dateString = lead.next_follow_up_date;
                        if (dateString.includes("-")) {
                          const parts = dateString.split("-");
                          if (parts[0].length === 2) {
                            dateString = `${parts[2]}-${parts[1]}-${parts[0]}`;
                          }
                        }
                        const date = new Date(dateString);
                        if (isNaN(date)) return "Invalid Date";
                        const day = String(date.getDate()).padStart(2, "0");
                        const month = String(date.getMonth() + 1).padStart(
                          2,
                          "0"
                        );
                        const year = date.getFullYear();
                        const formattedDate = `${day}-${month}-${year}`;
                        const today = new Date();
                        const todayDay = today.getDate();
                        const todayMonth = today.getMonth();
                        const todayYear = today.getFullYear();
                        let color = "black";
                        if (
                          date.getFullYear() === todayYear &&
                          date.getMonth() === todayMonth &&
                          date.getDate() === todayDay
                        ) {
                          color = "green";
                        } else if (date < today) {
                          color = "red";
                        }
                        return <span style={{ color }}>{formattedDate}</span>;
                      })()}
                    </td>
                    <td>
                      {lead.followup_time
                        ? (() => {
                            const followUpDate = new Date(
                              lead.next_follow_up_date
                            );
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            followUpDate.setHours(0, 0, 0, 0);
                            let color = "black";
                            if (followUpDate.getTime() === today.getTime()) {
                              color = "green";
                            } else if (followUpDate < today) {
                              color = "red";
                            }
                            const [hourStr, min] =
                              lead.followup_time.split(":");
                            let hour = parseInt(hourStr);
                            const ampm = hour >= 12 ? "PM" : "AM";
                            hour = hour % 12;
                            if (hour === 0) hour = 12;
                            const time12 = `${String(hour).padStart(
                              2,
                              "0"
                            )}:${min} ${ampm}`;
                            return <span style={{ color }}>{time12}</span>;
                          })()
                        : "-"}
                    </td>
                    <td className="d-flex gap-2">
                      <div className="dropdown">
                        <button
                          className="btn light btn-action dropdown-toggle"
                          type="button"
                          id={`dropdownMenuButton-${lead.id}`}
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          <BsThreeDots size={20} />
                        </button>
                        <ul
                          className="dropdown-menu"
                          aria-labelledby={`dropdownMenuButton-${lead.id}`}
                        >
                          <li className="dropdown-item">
                            <button
                              className="btn view_btn btn-sm me-1"
                              onClick={() => handleEditLead(lead.id)}
                              title="Update"
                            >
                              <MdSecurityUpdateGood className="me-2" /> Update
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="d-flex justify-content-end">
              <Pagination>
                <Pagination.Prev
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                />
                <Pagination.Item
                  active={1 === page}
                  onClick={() => handlePageChange(1)}
                >
                  1
                </Pagination.Item>
                {page > 3 && <Pagination.Ellipsis />}
                {page > 2 && (
                  <Pagination.Item onClick={() => handlePageChange(page - 1)}>
                    {page - 1}
                  </Pagination.Item>
                )}
                {page !== 1 && page !== totalPages && (
                  <Pagination.Item active>{page}</Pagination.Item>
                )}
                {page < totalPages - 1 && (
                  <Pagination.Item onClick={() => handlePageChange(page + 1)}>
                    {page + 1}
                  </Pagination.Item>
                )}
                {page < totalPages - 2 && <Pagination.Ellipsis />}
                {totalPages > 1 && (
                  <Pagination.Item
                    active={totalPages === page}
                    onClick={() => handlePageChange(totalPages)}
                  >
                    {totalPages}
                  </Pagination.Item>
                )}
                <Pagination.Next
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                />
              </Pagination>
            </div>
          </>
        )}
      </div>

      {/* CSV Upload Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Property Social Media Leads CSV</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <input
            type="file"
            accept=".csv"
            ref={inputRef}
            onChange={(e) => setFile(e.target.files[0])}
            style={{ display: "none" }}
          />
          <Button onClick={() => inputRef.current && inputRef.current.click()}>
            {file ? "Change CSV" : "Select CSV"}
          </Button>
          <p className="mt-2">{file ? file.name : "No file selected"}</p>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div className="downloadtemplate">
              <a href={callingleads} download="Lead Formate.csv">
                <Button variant="success">Download Excel Template</Button>
              </a>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="success"
                disabled={loading}
                onClick={handleUploadSubmit}
              >
                {loading ? "Uploading..." : "Upload"}
              </Button>
              <Button variant="danger" onClick={() => setShowModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Edit Lead Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Calling Leads</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={editData.status || ""}
                onChange={(e) =>
                  setEditData({ ...editData, status: e.target.value })
                }
                className="text-dark"
              >
                <option value="">All Status</option>
                <option value="Already Purchased">Already Purchased</option>
                <option value="Call not Pickup">Call not Pickup</option>
                <option value="Customer Self Call Back When He Will Come">
                  Customer Self Call Back When He Will Come
                </option>
                <option value="In Follow Up">In Follow Up</option>
                <option value="Details sent visit not done">
                  Details sent visit not done
                </option>
                <option value="In Follow Up Booking Done">
                  In Follow Up Booking Done
                </option>
                <option value="In Follow Up Booking Done Payment Pending">
                  In Follow Up Booking Done Payment Pending
                </option>
                <option value="In Follow Up Hot">In Follow Up Hot</option>
                <option value="In Follow Up Site Visit Done Booking Pending">
                  In Follow Up Site Visit Done Booking Pending
                </option>
                <option value="Invalid Mobile Number">
                  Invalid Mobile Number
                </option>
                <option value="Looking in Commercial Use">
                  Looking in Commercial Use
                </option>
                <option value="Looking in Different Location">
                  Looking in Different Location
                </option>
                <option value="Looking in Low Budget">
                  Looking in Low Budget
                </option>
                <option value="M Profile">M Profile</option>
                <option value="Not Interested">Not Interested</option>
                <option value="Not Responding">Not Responding</option>
                <option value="New">New</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Remark</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editData.remark}
                onChange={(e) =>
                  setEditData({ ...editData, remark: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Next Follow Up Date</Form.Label>
              <Form.Control
                type="date"
                value={editData.next_follow_up_date}
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    next_follow_up_date: e.target.value,
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Next Follow Up Time</Form.Label>
              <div className="d-flex">
                <Form.Control
                  type="time"
                  value={editData.followup_time || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      followup_time: e.target.value || null,
                    })
                  }
                  placeholder="NA"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary ms-2"
                  onClick={() =>
                    setEditData({ ...editData, followup_time: null })
                  }
                >
                  NA
                </button>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleUpdateLead}>
            Update
          </Button>
          <Button variant="danger" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default UploadPropertyLeadCSV;